import { MouseEvent as ReactMouseEvent, useCallback, useMemo } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { getContext } from '@picsart/miniapps-sdk';

import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import analytics, { OriginEnum } from 'pages/Batch/utils/analytics';
import { allowedUploadingTypes } from 'pages/Batch/configs';
import { isDev } from 'configs/constants';

import useMiniappHelpers from './useMiniappHelpers';

export enum UploadChangeEnumType {
  DROP = 'drop',
  CHANGE='change'
}

export type UseFileUploader = {
  onFileUploadDone: (files: File[], rejectedFiles: FileRejection[], isDrop?: boolean) => void;
  multiple?: boolean;
  maxCount?: number;
  accept?: string[];
}

const useFileUploader = ({ onFileUploadDone, multiple = true, maxCount = 0, accept = allowedUploadingTypes }: UseFileUploader) => {
  const { setIsLoading, isWeb } = useBatchData();
  const { miniappUploader } = useMiniappHelpers();

  const onFileDialogCancel = useCallback(() => analytics.uploadDone(OriginEnum.batch_editor, 'cancel'), []);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => {
    onFileUploadDone(acceptedFiles, rejectedFiles, event.type === UploadChangeEnumType.DROP);
  }, [onFileUploadDone]);

  const { getRootProps, getInputProps } = useDropzone({
    // do not provide maxFiles, cause we have custom limit error handling
    onFileDialogCancel,
    multiple,
    accept,
    onDrop,
  });

  const inputProps = useMemo(getInputProps, [getInputProps]);
  const rootProps = useMemo(getRootProps, [getRootProps]);

  const handleUploadClick = useCallback(async (ev: ReactMouseEvent<HTMLElement>) => {
    if (isDev || isWeb) {
      rootProps.onClick!(ev);
    } else {
      setIsLoading(true);
      const files = await miniappUploader(multiple, maxCount);
      if (!files.length) {
        await getContext().handlers.close();
        return;
      }
      onFileUploadDone(files, []);
      setIsLoading(false);
    }
  }, [isWeb, rootProps.onClick, setIsLoading, miniappUploader, onFileUploadDone, multiple, maxCount]);

  return {
    handleUploadClick,
    inputProps,
    rootProps,
  };
};

export default useFileUploader;
